import {
  createAtomConsumer,
  createAtomProvider,
  useAtomStore,
  useOptionalAtomStore,
} from "./react";
import type { ProviderProps, StoreConsumerProps } from "./react";
import type {
  NameStore,
  NameProvider,
  UseNameStore,
  NameConsumer,
  UseOptionalNameStore,
} from "./types";
import {
  getConsumerIndex,
  getProviderIndex,
  getStoreIndex,
  getUseOptionalStoreIndex,
  getUseStoreIndex,
} from "./utils";
import type React from "react";
import type { Class } from "utility-types";

export interface CreateAtomStoreOptions<N extends string, T extends object> {
  name?: N;
  StoreClass?: Class<T>;
  effect?: React.FC;
}

export type AtomStoreApi<N extends string, T extends object> = {
  name?: N;
} & Record<NameProvider<N>, React.FC<ProviderProps<T>>> &
  Record<UseNameStore<N>, (scope?: string) => T> &
  Record<UseOptionalNameStore<N>, (scope?: string) => T | undefined> &
  Record<NameConsumer<N>, React.FC<StoreConsumerProps<T>>> & {
    Provider: React.FC<ProviderProps<T>>;
    Consumer: React.FC<StoreConsumerProps<T>>;
    useStore: (scope?: string) => T;
    useOptionalStore: (scope?: string) => T | undefined;
    name: N;
  };

export function createAtomStore<T extends object, N extends string = string>(
  args?: CreateAtomStoreOptions<N, T> | Class<T> | N
): AtomStoreApi<N, T> {
  const {
    name: nameArg,
    StoreClass,
    effect,
  } = typeof args === "function"
    ? { StoreClass: args }
    : typeof args === "string"
      ? { name: args }
      : (args ?? {});

  const storeMap = new Map<string, T>();

  const name = nameArg ?? StoreClass?.name;

  if (!name) {
    throw new Error("Store name is required");
  }

  const providerIndex = getProviderIndex(name) as NameProvider<N>;
  const consumerIndex = getConsumerIndex(name) as NameConsumer<N>;
  const useStoreIndex = getUseStoreIndex(name) as UseNameStore<N>;
  const useOptionalStoreIndex = getUseOptionalStoreIndex(
    name
  ) as UseNameStore<N>;
  const storeIndex = getStoreIndex(name) as NameStore<N>;

  const useStore = (scope?: string) => {
    const store = useAtomStore<T>(name, scope);
    return store;
  };

  const useOptionalStore = (scope?: string) => {
    const store = useOptionalAtomStore<T>(name, scope);
    return store;
  };

  const Provider = createAtomProvider<N, T>({
    storeName: name as N,
    StoreClass,
    effect,
    onCreate: ({ scope, store }) => {
      if (storeMap.has(scope)) {
        throw new Error(`${storeIndex} with scope ${scope} already exists`);
      }
      storeMap.set(scope, store);
    },
    onRemove: ({ scope }) => {
      storeMap.delete(scope);
    },
  });

  const Consumer = createAtomConsumer(name, useStore);

  return {
    [providerIndex]: Provider,
    [useStoreIndex]: useStore,
    [useOptionalStoreIndex]: useOptionalStore,
    [consumerIndex]: Consumer,
    Provider,
    Consumer,
    useStore,
    useOptionalStore,
    name,
  } as AtomStoreApi<N, T>;
}
